import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { AddressTypeEnum } from "src/app/shared/Models/enums/AddressTypeEnum";
import { ExistingLomBuildingDto } from "src/app/shared/Models/read/ExistingLomBuildingDto";
import { GetLomBuildingsByAddressCriteriaQuery } from "src/app/shared/Models/read/GetLomBuildingsByAddressCriteriaQuery";
import { NewBuildingFilterDTO } from "src/app/shared/Models/read/NewBuildingFilterDTO";
import { AssignAddressToExistingBuildingDTO } from "src/app/shared/Models/write/AssignAddressToExistingBuildingDTO";
import { AssignAddressToNewBuildingDTO } from "src/app/shared/Models/write/AssignAddressToNewBuildingDTO";
import { ChangeBlockForBuildingsDto } from "src/app/shared/Models/write/ChangeBlockForBuildingsDto";
import { CommandResponse } from "src/app/shared/Models/write/CommandResponse";
import { DeleteAddressDTO } from "src/app/shared/Models/write/DeleteAddressDTO";
import { APIEndpoints } from "../../../../config/apiEndpoint.config";
import { HttpService } from "../../../shared/http.service";
import { Address } from "../../../shared/Models/Address";
import { BuildingSurveyViewModel } from "../../../shared/Models/buildingTableViewModel";
import { CreateBuildingDto } from "../../../shared/Models/write/CreateBuildingDto";
import { CreateBuildingWithExistingAddressDto } from "../../../shared/Models/write/createBuildingWithExistingAddressDto";
import { CreateOutsideSurveyDto } from "../../../shared/Models/write/CreateOutsideSurveyDto";
import { ExportBuildingActionsDto } from "../../../shared/Models/write/ExportBuildingActionsDto";
import { ApiEndpoints } from "src/app/shared/configuration";
import { EmptyBuilding } from "src/app/shared/Models/read/EmptyBuilding";

@Injectable({
  providedIn: "root",
})
export class BuildingService {
  constructor(private httpService: HttpService, private http: HttpClient) {}

  getBuildingSurveyList(): Observable<BuildingSurveyViewModel[]> {
    return this.httpService.get<BuildingSurveyViewModel[]>(
      APIEndpoints.getBuildingSurveyList
    );
  }

  getCommandTypeList(): Observable<string[]> {
    return this.httpService.get<string[]>(APIEndpoints.getCommandTypeList);
  }

  getBuildingListByAreaId(
    areaId: string
  ): Observable<BuildingSurveyViewModel[]> {
    return this.httpService
      .get(APIEndpoints.getBuildingListByAreaId + areaId)
      .pipe(
        map((res: any) => {
          return res.map((r) => new BuildingSurveyViewModel().deserialize(r));
        })
      );
  }

  changeEngineer(engineerId: string, surveyId: string, insideSurveyId: string) {
    return this.httpService.post(APIEndpoints.changeOutsideSurveyEngineer, {
      engineerId,
      surveyId,
      insideSurveyId,
    });
  }
  reassignOutsideEngineer(engineerId: string, outsideSurveyId: string) {
    return this.httpService.post(APIEndpoints.reassignOutsideSurveyEngineer, {
      engineerId,
      outsideSurveyId,
    });
  }
  reassignInsideEngineer(engineerId: string, insideSurveyId: string) {
    return this.httpService.post(APIEndpoints.reassignInsideSurveyEngineer, {
      engineerId,
      insideSurveyId,
    });
  }
  backSurveyToOutsideOngoing(
    insideSurveyId: string,
    buildingId: string,
    buildingGroupId: string
  ) {
    return this.httpService.post(APIEndpoints.backSurveyToOutsideOngoing, {
      insideSurveyId,
      buildingId,
      buildingGroupId,
    });
  }

  createBuilding(data: CreateBuildingDto): Observable<CommandResponse> {
    return this.httpService.post(APIEndpoints.createBuilding, data);
  }

  createBuildingWithExistingAddress(
    data: CreateBuildingWithExistingAddressDto
  ): Observable<CommandResponse> {
    return this.httpService.post(
      APIEndpoints.createBuildingWithExistingAddress,
      data
    );
  }

  createOutsideSurvey(data: CreateOutsideSurveyDto) {
    return this.httpService.post(
      APIEndpoints.createOutsideSurveyEngineer,
      data
    );
  }

  getBuildingCSV() {
    return this.httpService.getCSV(APIEndpoints.exportCSV);
  }

  getAllAddressesWithoutABuilding(areaId: string): Observable<Address[]> {
    return this.httpService.get(
      APIEndpoints.getAllAddressesWithoutABuilding + `/${areaId}`
    );
  }

  GetAllFiberhoodAddressesByAreaId(areaId: string): Observable<Address[]> {
    return this.httpService.get(
      APIEndpoints.getAllFiberhoodAddressesByAreaId + `/${areaId}`
    );
  }

  getNewBuildingFilterInfo(areaId: string): Observable<NewBuildingFilterDTO[]> {
    return this.httpService.get(
      APIEndpoints.getNewBuildingFilterAreaId + `/${areaId}`
    );
  }

  assignAddressToNewBuilding(data: AssignAddressToNewBuildingDTO) {
    return this.httpService.post(APIEndpoints.assignAddressToNewBuilding, data);
  }

  deleteAddress(data: DeleteAddressDTO) {
    return this.httpService.post(APIEndpoints.deleteAddress, data);
  }

  assignAddressToExistingBuilding(data: AssignAddressToExistingBuildingDTO) {
    return this.httpService.post(
      APIEndpoints.assignAddresToExistingBuilding,
      data
    );
  }

  changeBlockForBuildings(data: ChangeBlockForBuildingsDto) {
    return this.httpService.post(
      APIEndpoints.changeBlockForBuildings,
      data,
      true
    );
  }

  getLomBuildingsByAddressCriteria(
    query: GetLomBuildingsByAddressCriteriaQuery
  ): Observable<ExistingLomBuildingDto[]> {
    return this.httpService.post(
      APIEndpoints.getLomBuildingsByAddressCriteria,
      query
    );
  }

  exportBuildingActionCsv(data: ExportBuildingActionsDto) {
    return this.httpService.postWithFileResponse(
      APIEndpoints.exportBuildingAction,
      data
    );
  }

  getSortedAddressTable(
    addressTable: BuildingSurveyViewModel[]
  ): BuildingSurveyViewModel[] {
    return addressTable.sort((a, b) => {
      if (!a.isMainAddress && b.isMainAddress) return 1;
      if (a.isMainAddress && !b.isMainAddress) return -1;
      if (
        a.addressType === AddressTypeEnum.NotInUse &&
        b.addressType !== AddressTypeEnum.NotInUse
      )
        return 1;
      if (
        b.addressType === AddressTypeEnum.NotInUse &&
        a.addressType !== AddressTypeEnum.NotInUse
      )
        return -1;
      if (a.street > b.street) return 1;
      if (a.street < b.street) return -1;
      if (a.number > b.number) return 1;
      if (a.number < b.number) return -1;
      if (a.suffix === null) return -1;
      if (b.suffix === null) return 1;
      if (a.suffix > b.suffix) return 1;
      if (a.suffix < b.suffix) return 1;
    });
  }

  refreshBuilding(buildingId: string): Observable<object> {
    return this.httpService.post(APIEndpoints.refreshBuilding, { buildingId });
  }

  getEmptyBuildings(): Observable<EmptyBuilding[]> {
    return this.httpService.get(APIEndpoints.getEmptyBuildings, true);
  }
}
